import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Ref,
} from 'react';
import { Extension } from '@tiptap/core';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import type { MentionOptions } from '@tiptap/extension-mention';
import type { EditorView } from '@tiptap/pm/view';
import { MENTION_ALL_HANDLE, type BodyFormat } from '@leanin/shared';
import {
  MentionPopover,
  mentionDisplayName,
  type MentionAllGroup,
  type MentionPick,
  type MentionPopoverHandle,
} from '../feed/MentionPopover';
import type { MemberView } from '../../lib/queries/identity';
import {
  editorMarkdown,
  findMentionSuggestionMatch,
  richTextExtensions,
} from './rich-text-extensions';
import { LinkDialog } from './LinkDialog';
import { pastedLinkUrl } from './link-url';
import styles from './RichTextEditor.module.css';

// Rich text editor compound for member-facing composers (feed posts,
// comments, event descriptions). `value`/`onChange` speak the markdown
// dialect defined in rich-text-extensions.ts; the TipTap document is
// internal state. By default Enter inserts a paragraph and never submits —
// submit is the Cmd/Ctrl+Enter shortcut (`onSubmitShortcut`) or the caller's
// button. Chat-style composers pass `onEnterSubmit` instead: Enter sends and
// Shift+Enter breaks the line — except inside a list, where both add the next
// item (leaving the list from an empty one) and Cmd/Ctrl+Enter sends.
// The toolbar is a sibling component (RichTextToolbar) consumers place
// where their layout needs it, connected via `onEditorReady`.

// The dialect every body this editor produces is written in. Callers stamp
// their write payloads with it rather than repeating the literal: a stale
// 'v1' on one write path ships v2 markdown tagged v1, and the reader then
// renders the source text verbatim ("[label](url)" on screen). Removed with
// the v1 reader in PR C of the post-editor rework.
export const EDITOR_BODY_FORMAT: BodyFormat = 'v2';

// The next-item key inside a list. On an empty item, where splitListItem
// declines, it leaves the list — as Enter does in a document editor — instead
// of burying a hard break in the empty item.
function continueOrLeaveList(editor: Editor): boolean {
  return editor.commands.first(({ commands }) => [
    () => commands.splitListItem('listItem'),
    () => commands.liftListItem('listItem'),
  ]);
}

type MentionSuggestion = MentionOptions['suggestion'];
type SuggestionLifecycle = ReturnType<NonNullable<MentionSuggestion['render']>>;
type SuggestionProps = Parameters<NonNullable<SuggestionLifecycle['onStart']>>[0];
type SuggestionKeyDownProps = Parameters<
  NonNullable<SuggestionLifecycle['onKeyDown']>
>[0];

type MentionState = {
  readonly query: string;
  readonly anchorRect: DOMRect;
  readonly activeIndex: number;
  readonly command: SuggestionProps['command'];
  /** Replaces the typed `@query` run with plain text. */
  readonly replaceWithText: (text: string) => void;
};

// Snapshot of the editor range the link dialog is operating on, taken at
// open time. The dialog is modal, so the document cannot change while it is
// up and the positions stay valid until save/remove/cancel.
type LinkDialogState = {
  readonly from: number;
  readonly to: number;
  readonly text: string;
  /** Existing link href when opened on a link, null when adding. */
  readonly href: string | null;
};

const LINK_DIALOG_EXTENSION = 'richTextLinkDialog';

/** Open the link dialog of the RichTextEditor owning this TipTap instance.
 *  Exposed through extension storage so the sibling RichTextToolbar can
 *  trigger the same dialog the Cmd/Ctrl+K shortcut opens. */
export function openLinkDialog(editor: Editor): void {
  const storage = editor.storage[LINK_DIALOG_EXTENSION] as
    | { open: () => void }
    | undefined;
  storage?.open();
}

// LinkedIn parity: pasting a lone URL while text is selected turns the
// selection into a link instead of replacing it with the URL text. Runs on
// the raw view because handlePaste fires before React state settles.
function applyLinkToSelection(view: EditorView, href: string): boolean {
  const linkMark = view.state.schema.marks['link'];
  if (linkMark === undefined) return false;
  const { from, to } = view.state.selection;
  view.dispatch(view.state.tr.addMark(from, to, linkMark.create({ href })));
  return true;
}

function editorPlainText(editor: Editor): string {
  return editor.getText({ blockSeparator: '\n' });
}

export type RichTextEditorHandle = {
  /** Focus the editor, caret at the end. */
  focus: () => void;
  /** Empty the document (fires `onChange('')`). */
  clear: () => void;
  /** True when the document has no content. */
  isEmpty: () => boolean;
  /** Insert literal text at the caret (newlines become hard breaks); the
   *  text is NOT parsed as markdown. */
  insertText: (text: string) => void;
};

type Props = {
  /** Markdown source of truth. Fed to the editor on mount and whenever the
   *  caller changes it to something other than the editor's last output
   *  (draft restore, external reset). */
  readonly value: string;
  readonly onChange: (markdown: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** 'bare' (default) — borderless, for editors inside their own chrome.
   *  'field' — bordered input box beside native form controls. */
  readonly variant?: 'field' | 'bare';
  readonly minHeight?: number;
  readonly autoFocus?: boolean;
  /** Cmd/Ctrl+Enter. */
  readonly onSubmitShortcut?: () => void;
  /** Chat mode: plain Enter submits (never while an IME composition or the
   *  mention popover is open) and Shift+Enter inserts a line break. Inside a
   *  list both add the next item, or leave the list from an empty one, and
   *  Cmd/Ctrl+Enter submits. */
  readonly onEnterSubmit?: () => void;
  /** The document's plain text (blocks joined by newlines, no markdown
   *  escaping), on mount and after every document change — including
   *  external `value` loads, which `onChange` does not echo. For consumers
   *  that scan what the member typed (link-preview URL detection). */
  readonly onTextChange?: (text: string) => void;
  readonly ariaLabel?: string;
  readonly id?: string;
  /** Called with the clipboard's plain text before ProseMirror's own paste
   *  handling. Return true to consume the paste (nothing is inserted);
   *  false falls through to the editor's native rich paste. Runs inside the
   *  paste pipeline — intercepting from a DOM listener outside the editor
   *  is too late and double-inserts. `ctx.linkApplied` is true when the
   *  editor already turned a non-empty selection into a link for this paste
   *  (lone-URL clipboard) — the paste is consumed either way then, and the
   *  hook must not insert the text itself. */
  readonly onPasteText?: (
    text: string,
    ctx: { readonly linkApplied: boolean },
  ) => boolean;
  readonly handle?: Ref<RichTextEditorHandle>;
  /** Surfaces the TipTap instance for composing RichTextToolbar (and tears
   *  it down with null on unmount). */
  readonly onEditorReady?: (editor: Editor | null) => void;
  /** handle → display name for every mention the body can contain.
   *  Ref-stable (mutable) Map the editor grows as mentions are picked and
   *  reads when hydrating `@handle` tokens from `value` into chips. */
  readonly mentionNames?: Map<string, string>;
  /** Scope @-mention suggestions to people who can see this group's
   *  content. Undefined → anyone. */
  readonly mentionAudienceGroupId?: string;
  /** Member pinned to the top of the bare-"@" suggestion list (the post
   *  author, for a reply/comment). Undefined → no pin. */
  readonly mentionPriorityMember?: MemberView;
  /** The group "@all" addresses. Its presence IS the permission: set only
   *  for viewers who may use `@all` here (the server re-decides on write). */
  readonly mentionAllGroup?: MentionAllGroup;
  /** Off for surfaces whose bodies aren't mention-resolved server-side
   *  (resource comments) — typing `@` stays literal text, no popover.
   *  Fixed per surface: changing it after mount has no effect. */
  readonly mentionsEnabled?: boolean;
};

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  variant = 'bare',
  minHeight = 24,
  autoFocus = false,
  onSubmitShortcut,
  onEnterSubmit,
  onTextChange,
  ariaLabel,
  id,
  onPasteText,
  handle,
  onEditorReady,
  mentionNames,
  mentionAudienceGroupId,
  mentionPriorityMember,
  mentionAllGroup,
  mentionsEnabled = true,
}: Props) {
  const [mention, setMention] = useState<MentionState | null>(null);
  const mentionRef = useRef<MentionState | null>(null);
  mentionRef.current = mention;
  // Escape closes the popover for the rest of the current @-run; the
  // suggestion plugin keeps firing onUpdate while the caret sits in the
  // run, so without this flag the popover would reopen on the next
  // keystroke after a dismissal.
  const mentionDismissedRef = useRef(false);
  const popoverRef = useRef<MentionPopoverHandle>(null);
  const [linkDialog, setLinkDialog] = useState<LinkDialogState | null>(null);

  // The extension set (and the suggestion/shortcut callbacks it closes
  // over) is created once per mount; latest handler props are read through
  // refs so the editor never needs recreating.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSubmitShortcutRef = useRef(onSubmitShortcut);
  onSubmitShortcutRef.current = onSubmitShortcut;
  const onEnterSubmitRef = useRef(onEnterSubmit);
  onEnterSubmitRef.current = onEnterSubmit;
  const onTextChangeRef = useRef(onTextChange);
  onTextChangeRef.current = onTextChange;
  const onPasteTextRef = useRef(onPasteText);
  onPasteTextRef.current = onPasteText;
  const openLinkDialogRef = useRef<() => void>(() => {});
  const lastMarkdownRef = useRef(value);
  // Every markdown string handed to onChange that the caller has not yet
  // passed back as `value`, oldest first. See the value-sync effect below.
  const unechoedRef = useRef<string[]>([]);

  const extensions = useMemo(() => {
    const openMention = (props: SuggestionProps): void => {
      if (mentionDismissedRef.current) return;
      const rect = props.clientRect?.();
      if (rect === null || rect === undefined) {
        setMention(null);
        return;
      }
      setMention({
        query: props.query,
        anchorRect: rect,
        activeIndex: 0,
        command: props.command,
        replaceWithText: (text) => {
          props.editor.chain().focus().insertContentAt(props.range, text).run();
        },
      });
    };
    const handleMentionKey = ({ event }: SuggestionKeyDownProps): boolean => {
      const state = mentionRef.current;
      if (state === null) return false;
      if (event.key === 'Escape') {
        mentionDismissedRef.current = true;
        setMention(null);
        return true;
      }
      const popover = popoverRef.current;
      if (popover === null) return false;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const count = popover.itemCount();
        if (count === 0) return popover.isPending();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setMention((s) =>
          s !== null
            ? { ...s, activeIndex: (s.activeIndex + step + count) % count }
            : null,
        );
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        // Enter and Tab both accept the highlighted suggestion (Tab is the
        // completion key every typeahead offers). Consume while suggestions
        // are loading (a no-op beats a stray paragraph split / focus jump
        // just before the list appears); once the query is definitively
        // empty, the key falls through to the editor's own handling.
        if (popover.selectActive()) return true;
        return popover.isPending();
      }
      return false;
    };
    const submitShortcut = Extension.create({
      name: 'richTextSubmitShortcut',
      addKeyboardShortcuts() {
        return {
          // Chat mode sends on it too — the one send key that works from
          // inside a list, where Enter adds the next item.
          'Mod-Enter': () => {
            const submit = onSubmitShortcutRef.current ?? onEnterSubmitRef.current;
            if (submit === undefined) return false;
            submit();
            return true;
          },
          // Registered after the mention extension, so TipTap runs these
          // first — yield to the popover while it has (or is loading)
          // suggestions so Enter picks one; an empty `@query` still sends.
          Enter: () => {
            const submit = onEnterSubmitRef.current;
            if (submit === undefined || this.editor.view.composing) return false;
            const popover = popoverRef.current;
            if (
              mentionRef.current !== null &&
              popover !== null &&
              (popover.itemCount() > 0 || popover.isPending())
            ) {
              return false;
            }
            // A list is being written: Enter adds the next item (or leaves the
            // list from an empty one) and the message waits for Mod-Enter or
            // an Enter outside the list.
            if (this.editor.isActive('listItem')) return continueOrLeaveList(this.editor);
            submit();
            return true;
          },
          // Falls through to HardBreak's own Shift-Enter outside lists.
          'Shift-Enter': () => {
            if (onEnterSubmitRef.current === undefined) return false;
            if (!this.editor.isActive('listItem')) return false;
            return continueOrLeaveList(this.editor);
          },
        };
      },
    });
    // Storage carries the opener so the sibling RichTextToolbar (which only
    // holds the Editor instance) can open the same dialog as Cmd/Ctrl+K —
    // see the exported openLinkDialog().
    const linkDialogExtension = Extension.create({
      name: LINK_DIALOG_EXTENSION,
      addStorage() {
        return {
          open: () => {
            openLinkDialogRef.current();
          },
        };
      },
      addKeyboardShortcuts() {
        return {
          'Mod-k': () => {
            openLinkDialogRef.current();
            return true;
          },
        };
      },
    });
    return [
      ...richTextExtensions({
        ...(placeholder !== undefined ? { placeholder } : {}),
        ...(mentionNames !== undefined ? { mentionNames } : {}),
        ...(mentionsEnabled
          ? {
              mentionSuggestion: {
                char: '@',
                findSuggestionMatch: findMentionSuggestionMatch,
                render: () => ({
                  onStart: (props: SuggestionProps) => {
                    mentionDismissedRef.current = false;
                    openMention(props);
                  },
                  onUpdate: openMention,
                  onKeyDown: handleMentionKey,
                  onExit: () => {
                    mentionDismissedRef.current = false;
                    setMention(null);
                  },
                }),
              },
            }
          : {}),
      }),
      submitShortcut,
      linkDialogExtension,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- created once per mount by design; latest callbacks flow through refs
  }, []);

  const editor = useEditor({
    extensions,
    content: value,
    editable: !disabled,
    onCreate: ({ editor: ed }) => {
      onTextChangeRef.current?.(editorPlainText(ed));
    },
    onTransaction: ({ editor: ed, transaction }) => {
      if (transaction.docChanged) onTextChangeRef.current?.(editorPlainText(ed));
    },
    onUpdate: ({ editor: ed }) => {
      const markdown = editorMarkdown(ed);
      if (markdown === lastMarkdownRef.current) return;
      lastMarkdownRef.current = markdown;
      unechoedRef.current.push(markdown);
      onChangeRef.current(markdown);
    },
    editorProps: {
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        if (text.length === 0) return false;
        const url = pastedLinkUrl(text);
        const linkApplied =
          url !== null && !view.state.selection.empty
            ? applyLinkToSelection(view, url)
            : false;
        const intercept = onPasteTextRef.current;
        if (intercept === undefined) return linkApplied;
        // The hook still runs when the link was applied (so the consumer can
        // resolve unfurl previews / attachments), but the paste is consumed
        // regardless of its answer — the selection already became the link.
        const consumed = intercept(text, { linkApplied });
        return consumed || linkApplied;
      },
      attributes: {
        class: [styles['canvas'], variant === 'field' ? styles['field'] : undefined]
          .filter((c): c is string => c !== undefined)
          .join(' '),
        role: 'textbox',
        'aria-multiline': 'true',
        ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
        ...(id !== undefined ? { id } : {}),
      },
    },
  });

  // (Re)assigned every render so the Mod-k shortcut and toolbar opener
  // always see the current editor instance and dialog state.
  openLinkDialogRef.current = () => {
    if (editor === null || !editor.isEditable) return;
    const onLink = editor.isActive('link');
    // Editing an existing link operates on the whole link, even from a
    // collapsed cursor inside it — same as extending before setLink/unsetLink.
    if (onLink) editor.chain().extendMarkRange('link').run();
    const { from, to } = editor.state.selection;
    setLinkDialog({
      from,
      to,
      text: editor.state.doc.textBetween(from, to),
      href: onLink
        ? ((editor.getAttributes('link')['href'] as string | undefined) ?? null)
        : null,
    });
  };

  const closeLinkDialog = () => {
    setLinkDialog(null);
    editor?.commands.focus();
  };

  const saveLink = (label: string, href: string) => {
    if (editor === null || linkDialog === null) return;
    setLinkDialog(null);
    const { from, to } = linkDialog;
    const chain = editor.chain().focus().setTextSelection({ from, to });
    if (from !== to && label === linkDialog.text) {
      // Text untouched — mark the existing range so other inline marks
      // (bold, mentions) on the selection survive.
      chain.setLink({ href }).run();
      return;
    }
    chain
      .insertContent({
        type: 'text',
        text: label,
        marks: [{ type: 'link', attrs: { href } }],
      })
      .run();
  };

  const removeLink = () => {
    if (editor === null || linkDialog === null) return;
    setLinkDialog(null);
    editor
      .chain()
      .focus()
      .setTextSelection({ from: linkDialog.from, to: linkDialog.to })
      .unsetLink()
      .run();
  };

  // A brief lockout (a composer frozen while its send settles) blurs the
  // contenteditable; hand focus back on unlock if the member was typing.
  const focusedWhenDisabledRef = useRef(false);
  useEffect(() => {
    if (editor === null) return;
    if (disabled) focusedWhenDisabledRef.current = editor.isFocused;
    editor.setEditable(!disabled, false);
    if (!disabled && focusedWhenDisabledRef.current) {
      focusedWhenDisabledRef.current = false;
      editor.commands.focus();
    }
  }, [editor, disabled]);

  // Push external `value` changes (draft restore, reset) into the editor.
  // Echoes of the editor's own output are skipped so the caret never gets
  // clobbered while the user types. An echo can arrive stale: a render that
  // trails fast typing commits an earlier keystroke's markdown after the
  // editor has moved on. Comparing against the latest output alone read that
  // as a reset — setContent snapped the caret and any selection to the end —
  // so an echo is any string the editor emitted and the caller has not yet
  // passed back; reaching one acknowledges it and everything before it.
  useEffect(() => {
    if (editor === null || editor.isDestroyed) return;
    const echoAt = unechoedRef.current.indexOf(value);
    if (echoAt !== -1) {
      unechoedRef.current.splice(0, echoAt + 1);
      return;
    }
    if (value === lastMarkdownRef.current) return;
    unechoedRef.current = [];
    lastMarkdownRef.current = value;
    editor.commands.setContent(value, false);
  }, [editor, value]);

  useEffect(() => {
    if (onEditorReady === undefined) return;
    onEditorReady(editor);
    return () => {
      onEditorReady(null);
    };
  }, [editor, onEditorReady]);

  // Autofocus once the view is in the DOM, caret at the end, and bring the
  // field into view inside any scrolling ancestor (a reply box deep in the
  // post dialog's thread). TipTap's own `autofocus` defers to a timer and an
  // animation frame, and lost to focus moves made in between; an effect
  // runs before any ancestor dialog's autofocus, and again after a remount
  // re-attaches the view.
  useEffect(() => {
    if (!autoFocus || editor === null) return;
    editor.commands.setTextSelection(editor.state.doc.content.size);
    editor.view.focus();
    editor.view.dom.scrollIntoView({ block: 'nearest' });
  }, [autoFocus, editor]);

  useImperativeHandle(
    handle,
    () => ({
      focus: () => {
        editor?.commands.focus('end');
      },
      clear: () => {
        editor?.commands.clearContent(true);
      },
      isEmpty: () => editor?.isEmpty ?? true,
      insertText: (text: string) => {
        if (editor === null) return;
        const nodes = text.split('\n').flatMap((line, index) => [
          ...(index > 0 ? [{ type: 'hardBreak' as const }] : []),
          ...(line.length > 0 ? [{ type: 'text' as const, text: line }] : []),
        ]);
        if (nodes.length === 0) return;
        editor.chain().focus().insertContent(nodes).run();
      },
    }),
    [editor],
  );

  const selectMention = (pick: MentionPick): void => {
    const state = mentionRef.current;
    if (state === null) return;
    // `@all` is plain text everywhere it is shown — no chip in the editor or
    // in the rendered body — so picking it writes the literal token.
    if (pick.kind === 'all') {
      state.replaceWithText(`@${MENTION_ALL_HANDLE} `);
      setMention(null);
      return;
    }
    // Written into `mentionNames` so a later hydration of the same body
    // (draft restore, edit) re-chips the token instead of leaving the
    // `@handle` as literal text. The chip serializes back to `@<handle>`.
    const label = mentionDisplayName(pick.member);
    mentionNames?.set(pick.member.handle, label);
    state.command({ id: pick.member.handle, label });
    setMention(null);
  };

  return (
    <div
      className={styles['shell']}
      style={{ '--rte-min-height': `${minHeight}px` } as CSSProperties}
    >
      <EditorContent editor={editor} />
      {mention !== null ? (
        <MentionPopover
          handle={popoverRef}
          anchorRect={mention.anchorRect}
          query={mention.query}
          activeIndex={mention.activeIndex}
          onSelect={selectMention}
          onDismiss={() => {
            // Radix handles Escape/outside-click before the editor's key
            // handler sees it — mark the run dismissed here too, or the
            // next keystroke reopens the popover.
            mentionDismissedRef.current = true;
            setMention(null);
          }}
          {...(mentionAudienceGroupId !== undefined
            ? { audienceGroupId: mentionAudienceGroupId }
            : {})}
          {...(mentionPriorityMember !== undefined
            ? { priorityMember: mentionPriorityMember }
            : {})}
          {...(mentionAllGroup !== undefined ? { mentionAllGroup } : {})}
        />
      ) : null}
      {linkDialog !== null ? (
        <LinkDialog
          initialText={linkDialog.text}
          initialUrl={linkDialog.href ?? ''}
          isEditing={linkDialog.href !== null}
          onSave={saveLink}
          onRemove={removeLink}
          onClose={closeLinkDialog}
        />
      ) : null}
    </div>
  );
}
